import React, { useState, useRef, useEffect } from 'react'
import cn from 'classnames'
import { handleEnter } from './utils'
import styles from './Truncate.module.scss'

interface ToggleButtonProps {
  children: React.ReactNode
  className?: string
  onClick: () => void
  onKeyPress: (event: React.KeyboardEvent<HTMLButtonElement>) => void
}

const ToggleButton = ({
  children,
  className = '',
  onClick,
  onKeyPress,
}: ToggleButtonProps) => (
  <button
    onClick={onClick}
    onKeyPress={onKeyPress}
    className={cn(
      'mc-text--link mc-text--capitalize mc-p-0',
      className,
      styles.button,
    )}
  >
    {children}
  </button>
)

interface TruncateProps {
  text: string
  textStyle?: string
  maxLine: number
  showMoreText: string
  showLessText: string
}

export const Truncate = ({
  text,
  textStyle,
  maxLine,
  showMoreText,
  showLessText,
}: TruncateProps) => {
  const [isShowingMore, setIsShowingMore] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const textRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    const element = textRef.current
    if (!element) return undefined

    const checkTruncation = () => {
      setIsTruncated(element.scrollHeight > element.clientHeight)
    }

    checkTruncation()

    window.addEventListener('resize', checkTruncation)
    return () => window.removeEventListener('resize', checkTruncation)
  }, [text])

  const toggleIsShowingMore = () =>
    setIsShowingMore((_isShowingMore) => !_isShowingMore)

  const toggleButtonProps = {
    className: textStyle,
    onClick: toggleIsShowingMore,
    onKeyPress: handleEnter(toggleIsShowingMore),
  }

  if (!text) return null

  if (isShowingMore) {
    return (
      <p className={cn(styles.text, textStyle)}>
        {text}
        &nbsp;
        <ToggleButton {...toggleButtonProps}>{showLessText}</ToggleButton>
      </p>
    )
  }

  return (
    <div className={styles.text}>
      <p
        ref={textRef}
        className={cn(styles.text, styles.truncated, textStyle)}
        style={{
          display: '-webkit-box',
          WebkitLineClamp: maxLine,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {text}
      </p>
      {isTruncated && (
        <ToggleButton {...toggleButtonProps}>{showMoreText}</ToggleButton>
      )}
    </div>
  )
}
